import { useMemo } from 'react';
import { Plane, Trash2, Edit2, ArrowRight, ArrowLeftRight } from 'lucide-react';
import type { Flight } from '@/types';
import { formatDate } from '@/utils';

interface FlightListProps {
  flights: Flight[];
  onDeleteFlight: (flightId: string) => void;
  onEditFlight: (flight: Flight) => void;
  isLoading?: boolean;
}

export function FlightList({
  flights,
  onDeleteFlight,
  onEditFlight,
  isLoading,
}: FlightListProps) {
  // Sort flights by date in descending order (newest first)
  const sortedFlights = useMemo(() => {
    return [...flights].sort((a, b) => {
      return new Date(b.flightDate).getTime() - new Date(a.flightDate).getTime();
    });
  }, [flights]);

  if (isLoading) {
    return (
      <div className="glass rounded-xl md:rounded-2xl p-4 md:p-6">
        <h3 className="text-base md:text-lg font-semibold mb-4 flex items-center gap-2">
          <Plane className="w-4 h-4 md:w-5 md:h-5 text-primary-500" />
          Your Flights
        </h3>
        <div className="flex items-center justify-center py-8">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-500"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="glass rounded-xl md:rounded-2xl p-4 md:p-6">
      <h3 className="text-base md:text-lg font-semibold mb-4 flex items-center gap-2">
        <Plane className="w-4 h-4 md:w-5 md:h-5 text-primary-500" />
        Your Flights
      </h3>

      {flights.length === 0 ? (
        <div className="text-center py-8 text-white/50">
          <Plane className="w-10 h-10 md:w-12 md:h-12 mx-auto mb-3 opacity-30" />
          <p className="text-sm md:text-base">No flights added yet.</p>
          <p className="text-xs md:text-sm">Add your first flight above!</p>
        </div>
      ) : (
        <div className="flight-scroll mobile-scroll space-y-3 max-h-[300px] md:max-h-[400px] overflow-y-auto pr-2">
          {sortedFlights.map((flight) => (
            <FlightCard
              key={flight.id}
              flight={flight}
              onDelete={() => onDeleteFlight(flight.id)}
              onEdit={() => onEditFlight(flight)}
            />
          ))}
        </div>
      )}
    </div>
  );
}

interface FlightCardProps {
  flight: Flight;
  onDelete: () => void;
  onEdit: () => void;
}

function FlightCard({ flight, onDelete, onEdit }: FlightCardProps) {
  const isReturn = flight.flightType === 'return';

  return (
    <div className="group relative bg-white/5 hover:bg-white/10 rounded-xl p-3 md:p-4 transition-all animate-fade-in">
      <div className="flex items-center justify-between">
        {/* Route */}
        <div className="flex items-center gap-2 md:gap-3">
          <span className="text-base md:text-xl font-bold text-primary-500">
            {flight.fromAirport.iata}
          </span>
          <span className="text-white/50">
            {isReturn ? (
              <ArrowLeftRight className="w-4 h-4 md:w-5 md:h-5" />
            ) : (
              <ArrowRight className="w-4 h-4 md:w-5 md:h-5" />
            )}
          </span>
          <span className="text-base md:text-xl font-bold text-primary-500">
            {flight.toAirport.iata}
          </span>
        </div>

        {/* Edit and Delete buttons */}
        <div className="flex gap-1">
          <button
            onClick={onEdit}
            className="opacity-70 md:opacity-0 md:group-hover:opacity-100 p-2 text-white/50 hover:text-blue-400 hover:bg-blue-500/20 rounded-lg transition-all"
            title="Edit flight"
          >
            <Edit2 className="w-4 h-4" />
          </button>
          <button
            onClick={onDelete}
            className="opacity-70 md:opacity-0 md:group-hover:opacity-100 p-2 text-white/50 hover:text-red-400 hover:bg-red-500/20 rounded-lg transition-all"
            title="Delete flight"
          >
            <Trash2 className="w-4 h-4" />
          </button>
        </div>
      </div>

      {/* Details */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between mt-2 text-xs md:text-sm text-white/60 gap-1 sm:gap-0">
        <span className="truncate">
          {flight.fromAirport.city} → {flight.toAirport.city}
        </span>
        <div className="flex items-center gap-2 md:gap-3">
          {isReturn && (
            <span className="text-xs px-2 py-0.5 rounded-full bg-primary-500/20 text-primary-400">
              Return
            </span>
          )}
          <span>{formatDate(flight.flightDate)}</span>
        </div>
      </div>

      {/* Description/Notes */}
      {flight.notes && (
        <div className="mt-2 text-sm text-white/70 italic">
          {flight.notes}
        </div>
      )}
    </div>
  );
}
